import React from 'react';
import styled from 'astroturf/react';
import { SwitchTransition, Transition } from 'react-transition-group';
import pluralize from 'pluralize';

import {
  FADE_DURATION,
  CountText,
  TransitionState,
  transitions,
} from './fadeTransition';
import LoadingDots from './LoadingDots';
import PremiumIcon from 'js/premium/PremiumIcon';
import { format } from './util';

export enum TAB {
  Titles = 0,
  FullText = 1,
  People = 2,
  // Translations (enum 3) has been removed
  Videos = 4,
  Courses = 5,
}

/** If `null` is passed in, we show a loading indicator */
type Counts = Record<TAB, number | null>;

type Props = {
  // Allows external styling
  className?: string;
  // Number of results found for each type
  counts: Counts;
  // Index of the currently selected tab
  current: number;
  // When user switches tabs
  onChange: (index: TAB) => void;
  // Whether user has access to fulltext via onboarding overhaul
  onboardingOverhaulFulltext?: boolean;
};

type TabProps = {
  active?: boolean;
  count?: number | null;
  lock?: boolean;
  onClick: () => void;
  // Whether this tab is premium only
  // Adds premium lock icon
  premium?: boolean;
  title: React.ReactNode;
};

const MOBILE_WIDTH = 750;

const Container = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  margin-bottom: -1px;

  @media (max-width: ${MOBILE_WIDTH}px) {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
  }
`;

const DesktopText = styled.div`
  display: block;

  @media (max-width: ${MOBILE_WIDTH}px) {
    display: none;
  }
`;

const MobileText = styled.div`
  display: none;

  @media (max-width: ${MOBILE_WIDTH}px) {
    display: block;
  }
`;

// The (Full Text) part of the middle tab looks weird on mobile
// if it breaks in the middle
const NoBreak = styled.span`
  display: inline-block;
`;

const Subtitle = styled.h3<{ active?: boolean }>`
  color: #333;
  font-size: 12px;
  font-weight: 400;
  margin: 0;
  opacity: 0.7;

  &.active {
    opacity: 1;
  }

  @media (max-width: ${MOBILE_WIDTH}px) {
    display: none;
  }
`;

const TabContainer = styled.li<{ active?: boolean }>`
  align-items: center;
  border: 1px solid #e0e0e0;
  border-bottom: 0;
  color: #444;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 175px;
  padding: 10px 30px;
  transition: 160ms background;
  z-index: 1;

  &.active {
    background: #f3f3f3;
    color: #094ec3;
    margin-bottom: -1px;
  }

  &:not(:last-of-type) {
    margin-right: 7px;
  }

  @media (max-width: ${MOBILE_WIDTH}px) {
    border: 0;
    min-width: unset;
    padding: 15px 0;
    text-align: center;

    &.active {
      background: #fff;
      border-bottom: 3px solid #094ec3;
    }

    &:not(:last-of-type) {
      margin-right: 0;
    }
  }
`;

const Title = styled.h2<{ active?: boolean }>`
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 6px;
  text-transform: uppercase;

  &.active {
    color: #094ec3;
    font-weight: 500;
  }

  @media (max-width: ${MOBILE_WIDTH}px) {
    color: #666;
    font-size: 14px;
    font-weight: 400;
    margin: 0;
  }
`;

// Used for getting click sources (pluralized for data consistency) and tab text
const tabTypeToName = (tab: TAB): string => {
  switch (tab) {
    case TAB.Titles:
      return 'Paper Titles';
    case TAB.FullText:
      return 'Full Text Papers';
    case TAB.People:
      return 'People';
    case TAB.Videos:
      return 'Videos';
    case TAB.Courses:
      return 'Courses';
  }
};

export const tabText = (
  tab: TAB,
  numResults: number,
  filtered: boolean,
): string => {
  return filtered
    ? format(numResults) + ' ' + pluralize('filtered result', numResults)
    : format(numResults) + ' ' + pluralize(tabTypeToName(tab), numResults);
};

const Tab = ({ active, count, lock = false, onClick, title }: TabProps) => (
  <TabContainer active={active} onClick={onClick}>
    <Title active={active}>{title}</Title>
    <Subtitle active={active}>
      {lock && <PremiumIcon size={17} />}
      {typeof count !== 'undefined' && (
        <SwitchTransition>
          <Transition key={(count == null).toString()} timeout={FADE_DURATION}>
            {(state: TransitionState) =>
              count != null ? (
                <CountText style={transitions[state]}>
                  {format(count)} {pluralize('Results', count)}
                </CountText>
              ) : (
                <LoadingDots
                  size={6}
                  style={{
                    ...transitions[state],
                    // Have to put the transition in the style here
                    // since the way LoadingDots is written, using styled(LoadingDots)
                    // affects the individual dots, not the container
                    transition: `${FADE_DURATION}ms opacity`,
                  }}
                />
              )
            }
          </Transition>
        </SwitchTransition>
      )}
    </Subtitle>
  </TabContainer>
);

const Tabs = ({
  className,
  counts,
  current,
  onChange,
  onboardingOverhaulFulltext = false,
}: Props): JSX.Element => {
  return (
    <Container className={className}>
      <Tab
        active={current === TAB.Titles}
        count={counts[TAB.Titles]}
        onClick={() => onChange(TAB.Titles)}
        title="Paper Titles"
      />
      <Tab
        active={current === TAB.FullText}
        count={counts[TAB.FullText]}
        lock={!onboardingOverhaulFulltext}
        onClick={() => onChange(TAB.FullText)}
        title={
          <React.Fragment>
            <DesktopText>
              Papers <NoBreak>(Full Text)</NoBreak>
            </DesktopText>
            <MobileText>
              <PremiumIcon size={17} />
              Full Text
            </MobileText>
          </React.Fragment>
        }
      />
      <Tab
        active={current === TAB.People}
        count={counts[TAB.People]}
        onClick={() => onChange(TAB.People)}
        title="People"
      />
      <Tab
        active={current === TAB.Videos}
        count={counts[TAB.Videos]}
        onClick={() => onChange(TAB.Videos)}
        title="Videos"
      />
      <Tab
        active={current === TAB.Courses}
        count={counts[TAB.Courses]}
        onClick={() => onChange(TAB.Courses)}
        title="Courses"
      />
    </Container>
  );
};

export default Tabs;
